/*
 * Ankur Mursalin
 *
 * https://encryptioner.github.io/
 *
 * Created on Thu Aug 04 2022
 */

import {
  defaultsDeep,
} from 'lodash';
import {
  defineStore,
} from 'pinia';
import {
  getAppInfo,
} from 'utilities';
import {
  CONSTANTS,
  defaultStyleConfig,
  defaultViewConfig,
  identifyDeviceType,
  viewportBreakpoint,
} from '@/helpers';
import {
  useAuthStore,
} from '@/store/auth';
import {
  useBackendStore,
} from '@/store/backend';
import type {
  IScreenBreakpoint,
  IStyleConfig,
  IViewConfig,
} from '@/types';

// eslint-disable-next-line import/prefer-default-export
export const useAppStore = defineStore('app', {
  state: () => ({
    device: {
      isMobile: false,
      // iPad pro will not be identified as mobile device,
      // but it is a touch device
      isTouchDevice: false,
      isSafari: false,
      isChrome: false,
      isFirefox: false,
      isMac: false,
      isWindows: false,
      isLinux: false,
      isIPad: false,
      isIPhone: false,
      isAndroidTablet: false,
      isAndroid: false,
    },
    breakpoint: '' as IScreenBreakpoint,
    prevRoutePath: '',
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    isBootstrapped: false,
    isLoading: false,
    styleConfig: structuredClone(defaultStyleConfig) satisfies IStyleConfig,
    viewConfig: structuredClone(defaultViewConfig) satisfies IViewConfig,
  }),
  getters: {
    homeRoute(): string {
      return CONSTANTS.ROUTE.DEFINED.GUEST_HOME;
    },
  },
  actions: {
    async init(): Promise<void> {
      if (this.isBootstrapped) {
        return;
      }

      await Promise.all([
        useBackendStore().fetchClientCompany(),
        this._setCompanyViewConfig(),
        this._setCompanyStyleConfig(),
      ]);

      await useAuthStore().localLogin();

      const device = identifyDeviceType();
      this.breakpoint = viewportBreakpoint();
      this.device = device;
      this.isBootstrapped = !!useBackendStore().clientCompany._id.length;
    },

    async _setCompanyViewConfig(): Promise<void> {
      try {
        const { cloudfrontPublicPath } = getAppInfo().CONFIG;
        const url = `${cloudfrontPublicPath}/${window.location.host}/configs/view.config.json`;

        const response = await fetch(url);

        if (!response.ok) {
          console.error(`Failed to fetch view configuration: ${response.status} ${response.statusText}`);
          return;
        }

        const viewConfig: IViewConfig = await response.json();

        this.viewConfig = defaultsDeep(viewConfig, defaultViewConfig);
      } catch (error) {
        console.error('Error loading domain view configuration:', error);
      }
    },

    async _setCompanyStyleConfig(): Promise<void> {
      try {
        const { cloudfrontPublicPath } = getAppInfo().CONFIG;
        const url = `${cloudfrontPublicPath}/${window.location.host}/configs/style.config.json`;

        const response = await fetch(url);

        if (!response.ok) {
          console.error(`Failed to fetch style configuration: ${response.status} ${response.statusText}`);
          return;
        }

        const styleConfig: IStyleConfig = await response.json();

        this.styleConfig = defaultsDeep(styleConfig, defaultStyleConfig);
      } catch (error) {
        console.error('Error loading domain style configuration:', error);
      }
    },
  },
  persist: {
    storage: window.sessionStorage,
  },
});
